import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function ReportsView({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('month');
  const [reportData, setReportData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [moduleUtilization, setModuleUtilization] = useState(null);
  const [moduleUtilLoading, setModuleUtilLoading] = useState(false);

  const loadReports = async (selectedPeriod = period) => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getReports(selectedPeriod, null, token);
      setReportData(data);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError(err.response?.data?.msg || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [token]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    loadReports(newPeriod);
  };

  const loadModuleUtilization = async () => {
    if (moduleUtilization) return; // already loaded
    try {
      setModuleUtilLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/clinical-supervisor/gen2/module-utilization`, {
        headers: { 'x-auth-token': token }
      });
      if (!response.ok) throw new Error('Failed to fetch module utilization');
      const data = await response.json();
      setModuleUtilization(data.modules || []);
    } catch (err) {
      console.error('Error loading module utilization:', err);
    } finally {
      setModuleUtilLoading(false);
    }
  };

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    if (newValue === 2) loadModuleUtilization();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const { overview, therapistBreakdown, topicTrends, periodStart, periodEnd } = reportData || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/clinical-supervisor/dashboard')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5">Reports & Analytics</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(periodStart)} - {formatDate(periodEnd)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} label="Period" onChange={(e) => handlePeriodChange(e.target.value)}>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">This Quarter</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={() => loadReports()}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Overview Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <AssignmentIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h4">{overview?.totalConsultations || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Consultations
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {overview?.completedConsultations || 0} completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PeopleIcon color="success" fontSize="large" />
                <Box>
                  <Typography variant="h4">{overview?.activeClients || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Clients
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {overview?.totalTherapists || 0} therapists
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TimeIcon color="info" fontSize="large" />
                <Box>
                  <Typography variant="h4">
                    {formatDuration(overview?.avgConsultationDuration)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Duration
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDuration(overview?.totalConsultationTime)} total
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingIcon color="warning" fontSize="large" />
                <Box>
                  <Typography variant="h4">{overview?.pendingCareNotes || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Reviews
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {overview?.approvedCareNotes || 0} approved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Therapist Performance" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Topic Trends" icon={<TrendingIcon />} iconPosition="start" />
          <Tab label="Gen2 Module Utilization" icon={<AssessmentIcon />} iconPosition="start" />
        </Tabs>

        {/* Therapist Performance Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Therapist Performance
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {therapistBreakdown && therapistBreakdown.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Therapist</TableCell>
                    <TableCell align="center">Active Clients</TableCell>
                    <TableCell align="center">Consultations</TableCell>
                    <TableCell align="center">Completed</TableCell>
                    <TableCell align="center">Avg Duration</TableCell>
                    <TableCell align="center">Care Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {therapistBreakdown.map((therapist) => (
                    <TableRow key={therapist.therapistId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {therapist.therapistName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{therapist.activeClientCount}</TableCell>
                      <TableCell align="center">{therapist.totalConsultations}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={therapist.completedConsultations}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {formatDuration(therapist.avgConsultationDuration)}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Chip
                            label={`${therapist.approvedCareNotes} approved`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          {therapist.pendingCareNotes > 0 && (
                            <Chip
                              label={`${therapist.pendingCareNotes} pending`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(`/clinical-supervisor/therapists/${therapist.therapistId}`)
                          }
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No therapist data available for this period
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Topic Trends Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Most Common Consultation Topics
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {topicTrends && topicTrends.length > 0 ? (
            <Grid container spacing={2}>
              {topicTrends.slice(0, 10).map((topic, index) => (
                <Grid item xs={12} sm={6} md={4} key={topic.topic}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight="medium" gutterBottom>
                            {topic.topic}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rank #{index + 1}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h5" color="primary">
                            {topic.count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            mentions
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No topic data available for this period
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Gen2 Module Utilization Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Gen2 Module Utilization
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {moduleUtilLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : moduleUtilization && moduleUtilization.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Module</TableCell>
                    <TableCell align="center">Consultations</TableCell>
                    <TableCell align="center">Applied</TableCell>
                    <TableCell align="center">Avg Latency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {moduleUtilization.map((mod) => (
                    <TableRow key={mod.moduleKey} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {mod.moduleKey}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{mod.consultCount}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={mod.appliedCount}
                          size="small"
                          color={mod.appliedCount > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {mod.avgLatencyMs != null ? `${Math.round(mod.avgLatencyMs)}ms` : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No Gen2 module utilization data available
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Additional Insights Section */}
      {overview && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Period Insights
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Consultation Completion Rate
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" color="success.main">
                    {overview.totalConsultations > 0
                      ? Math.round(
                          (overview.completedConsultations / overview.totalConsultations) * 100
                        )
                      : 0}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({overview.completedConsultations} / {overview.totalConsultations})
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Care Note Approval Rate
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" color="info.main">
                    {overview.approvedCareNotes + overview.pendingCareNotes > 0
                      ? Math.round(
                          (overview.approvedCareNotes /
                            (overview.approvedCareNotes + overview.pendingCareNotes)) *
                            100
                        )
                      : 0}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({overview.approvedCareNotes} approved)
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
}

export default ReportsView;
